import { useMeQueries } from 'hooks-queries/me'

import { ME_ERROR_LOG } from './use-me.consts'
import type { IUseMeParams } from './use-me.types'
import { handleMeCompleted, isStatusMessage, isUser } from './use-me.utils'

/**
 * Auth-bootstrap hook for the authenticated user. Fired once when Keycloak reports the user
 * authenticated (skipped otherwise); downstream reads should use the Apollo cache / UserStore, not
 * this hook. Owns the on-login side effects (GTM / `localStorage` / Datadog) via `onCompleted`
 * (`handleMeCompleted`), and surfaces `hasStatusMessage` so the caller can render an error state
 * without re-decoding the union, and `user` so it can read the authenticated user's own fields
 * without re-decoding it either.
 */
export const useMe = ({ authenticated }: IUseMeParams) => {
  const result = useMeQueries({
    queryName: 'getMe',
    errorLog: ME_ERROR_LOG,
    // Auth bootstrap runs before any workspace is selected, so it must opt out of the
    // `x-insights-workspace` header (see the authLink). The server's `@authorized` directive on
    // `me` rejects the *whole* query when that header points at a workspace this user can't access
    // — e.g. a stale/foreign `panelId` left in localStorage — which would blank the app behind
    // AuthGate's error screen.
    options: { skip: !authenticated, onCompleted: handleMeCompleted, context: { skipWorkspaceHeader: true } },
  })

  const me = result.data?.me

  return { ...result, hasStatusMessage: isStatusMessage(me), user: isUser(me) ? me : undefined }
}
